import { Event, EventTypeEnum } from '../../../graph-queries/__generated__/graphql';
import { FCC } from '../../../lib/types';
import { useReadinessCheck, useReadinessHistory } from './ReadinessCheck.util';
import { tcpCurveTargets, ocpCurveTargets } from './ReadinessCheck.constants';
import { CartesianGrid, ComposedChart, Line, Scatter, Tooltip, XAxis, YAxis } from 'recharts';
import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  CREATE_READINESS_METRIC,
  GET_READINESS_METRICS_BY_EVENT,
} from '../../../graph-queries/tar/readinessMetric';
import { useThrottle } from '../../../lib/util/timer';

const ReadinessCheckDetails: FCC<{ event: Event }> = ({ event }) => {
  const history = useReadinessHistory(event);

  const sCurveTargets = event.eventType === EventTypeEnum.Tar ? tcpCurveTargets : ocpCurveTargets;
  const readiness = useReadinessCheck(event);
  const [readinessUpdateCount, setReadinessUpdateCount] = useState(0);

  const [mutate, { loading, error }] = useMutation(CREATE_READINESS_METRIC, {
    refetchQueries: [GET_READINESS_METRICS_BY_EVENT],
  });
  const createReadiness = useThrottle(mutate, 1000);

  const availableHistoryWeekNumbers = history.data.map((h) => h.weekNumber);
  const data = (sCurveTargets.BASELINE || []).map((target, index) => {
    const weekNumber = index - 52;
    const d: {
      target: number;
      readinessHistory?: number;
      readinessPerc?: number;
      weekNumber: number;
    } = {
      target,
      weekNumber,
    };

    // Check with current data
    if (weekNumber === readiness.weekNumber) {
      d.readinessPerc = readiness.readinessPerc;
    }
    // Match with history data
    else if (availableHistoryWeekNumbers.includes(weekNumber)) {
      d.readinessHistory = history.data.find((h) => h.weekNumber === weekNumber)?.readinessPerc;
    }

    return d;
  });

  useEffect(() => {
    if (loading || readiness.loading || history.loading) return;
    if (error || readiness.error || history.error || !history.data) return;

    const existingReadiness = history.data.find((d) => d.weekNumber === readiness.weekNumber);
    if (!existingReadiness || readiness.readinessPerc > existingReadiness.readinessPerc) {
      readinessUpdateCount < 10 && // Additional safety checkpoint
        createReadiness({
          variables: {
            input: {
              readinessPerc: readiness.readinessPerc,
              eventId: event.eventId,
            },
          },
        })
          .then(() => {
            setReadinessUpdateCount((v) => v + 1);
          })
          .catch(() => setReadinessUpdateCount(11));
    }
  }, [readiness, history, loading, error]);

  return (
    <div>
      <ComposedChart
        width={800}
        height={300}
        data={data}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="weekNumber" type="number" domain={[-54, 3]} tickCount={20} />
        <YAxis />
        <Tooltip
          defaultIndex={readiness.weekNumber + 52}
          cursor={false}
          active
          labelFormatter={(label) => `Week: ${label}`}
          formatter={(value, name) => `${name === 'Readiness' ? Number(value).toFixed(2) : value}%`}
        />
        <Line
          name="Target"
          type="monotone"
          dataKey="target"
          stroke="#F6C243"
          strokeWidth={3}
          activeDot={{ r: 6 }}
          dot={false}
        />

        <Line
          name="Readiness"
          type="monotone"
          dataKey="readinessHistory"
          stroke="red"
          strokeWidth={3}
          activeDot={{ r: 6 }}
          dot={false}
        />
        <Scatter name="Readiness" dataKey="readinessPerc" fill="red" />
      </ComposedChart>
    </div>
  );
};

export default ReadinessCheckDetails;
